import { useCallback, useRef } from 'react';
import { dealroomIdText, dealroomInvestorUrl } from '@nemo/core/model';
import { candidateTypeLabel, formatHqCountries } from '../dealroom/format.js';
import type { Candidate } from '../dealroom/types.js';
import { formatDay } from '../ui/format.js';
import { SiteIcon, siteOf } from '../ui/SiteIcon.js';
import { CoInvestLine } from './CoInvestLine.js';
import { FactLine } from './FactLine.js';
import { useSwipe, type SwipeDirection } from './useSwipe.js';

/** What a company the candidate backed says it does. */
export interface CandidateExample {
  name: string;
  tldr: string | null;
  website: string | null;
}

export interface CandidateCardProps {
  candidate: Candidate;
  examples: readonly CandidateExample[];
  /** 0 is the card being decided; 1 and 2 are the two peeking behind it. */
  depth: number;
  /** Only the top card follows the hand, and only while nothing is in flight. */
  interactive: boolean;
  /** The way a card already decided is leaving, if it is leaving. */
  leaving: SwipeDirection | null;
  onSwipe: (direction: SwipeDirection) => void;
}

/** How steeply the drag leans the card over, and how far it is allowed to. */
const DRAG_TILT_PER_PX = 1 / 14;
const DRAG_TILT_CAP = 12;

/**
 * One candidate, big enough to decide on. The card leans towards the cursor and
 * carries a moving foil, which is doing a job rather than decoration: it is the
 * signal that this thing is picked up and thrown rather than clicked.
 *
 * Every one of those effects is written as a custom property on the element
 * itself, so following a hand or a cursor costs no React render.
 */
export function CandidateCard(props: CandidateCardProps): React.JSX.Element {
  const slot = useRef<HTMLDivElement | null>(null);
  const card = useRef<HTMLDivElement | null>(null);

  const onMove = useCallback((offset: { dx: number; dy: number }) => {
    const node = slot.current;
    if (node === null) return;
    const tilt = Math.max(-DRAG_TILT_CAP, Math.min(DRAG_TILT_CAP, offset.dx * DRAG_TILT_PER_PX));
    node.style.setProperty('--drag-x', `${offset.dx}px`);
    node.style.setProperty('--drag-y', `${offset.dy}px`);
    node.style.setProperty('--drag-rot', `${tilt}deg`);
  }, []);

  const swipe = useSwipe({ onSwipe: props.onSwipe, onMove, enabled: props.interactive });

  const trackCursor = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const node = card.current;
    if (node === null) return;
    const box = node.getBoundingClientRect();
    node.style.setProperty('--x', String(event.clientX - box.left));
    node.style.setProperty('--y', String(event.clientY - box.top));
    node.style.setProperty('--w', String(box.width));
    node.style.setProperty('--h', String(box.height));
  }, []);

  const recentre = useCallback(() => {
    const node = card.current;
    if (node === null) return;
    const box = node.getBoundingClientRect();
    node.style.setProperty('--x', String(box.width / 2));
    node.style.setProperty('--y', String(box.height / 2));
    node.style.setProperty('--w', String(box.width));
    node.style.setProperty('--h', String(box.height));
  }, []);

  const { candidate } = props;
  const hq = formatHqCountries(candidate.hqCountries);
  const dealroomUrl = dealroomInvestorUrl(candidate.dealroomPath);
  const classes = [
    'decide-slot',
    `depth-${props.depth}`,
    swipe.state.dragging ? 'dragging' : '',
    swipe.state.past === null ? '' : `past-${swipe.state.past}`,
    props.leaving === null ? '' : `leaving leaving-${props.leaving}`,
  ]
    .filter((name) => name !== '')
    .join(' ');

  return (
    <div className={classes} ref={slot} aria-hidden={props.depth > 0}>
      <div
        className="decide-card"
        ref={card}
        onMouseMove={trackCursor}
        onMouseLeave={recentre}
        onDragStart={(event) => event.preventDefault()}
        {...(props.interactive ? swipe.handlers : {})}
      >
        <div className="decide-sheen" aria-hidden="true" />
        <div className="decide-holo" aria-hidden="true" />

        <div className="decide-body">
          <header className="decide-head">
            <h2>
              <SiteIcon site={siteOf(candidate.website)} name={candidate.name} size={20} />
              {candidate.name}
            </h2>
            <div className="decide-identity">
              <span className="chip">{candidateTypeLabel(candidate.investorType)}</span>
              {hq.shown.map((chip) => (
                <span className="chip" key={chip.code}>
                  {chip.label}
                </span>
              ))}
              {hq.overflow !== null && (
                <span className="chip" title={hq.overflow.title}>
                  {hq.overflow.label}
                </span>
              )}
              <span className="chip">
                {candidate.roundsSeen === 1
                  ? '1 round seen'
                  : `${candidate.roundsSeen} rounds seen`}
              </span>
            </div>
            <CoInvestLine candidate={candidate} names={4} inDeck />
            <FactLine candidate={candidate} />
          </header>

          <div className="decide-examples">
            {props.examples.length === 0 && candidate.exampleCompanies.length === 0 && (
              <p className="subtle">No companies recorded against this name yet.</p>
            )}
            {(props.examples.length > 0
              ? props.examples
              : candidate.exampleCompanies.map((name) => ({ name, tldr: null, website: null }))
            ).map((example) => (
              <div className="decide-example" key={example.name}>
                {example.website === null ? (
                  <strong>{example.name}</strong>
                ) : (
                  <a href={example.website} target="_blank" rel="noreferrer" draggable={false}>
                    <strong>{example.name}</strong>
                  </a>
                )}
                <span className="subtle" title={example.tldr ?? undefined}>
                  {example.tldr ?? '—'}
                </span>
              </div>
            ))}
          </div>

          <footer className="decide-foot">
            <span className="subtle">
              Seen {formatDay(candidate.firstSeen) ?? '—'} to {formatDay(candidate.lastSeen) ?? '—'}
            </span>
            {candidate.dealroomId === null ? (
              // Not from a round at all: somebody moved here.
              <span>{candidate.note ?? 'Not in Dealroom'}</span>
            ) : dealroomUrl === null ? (
              // Dealroom's pages are addressed by a slug we do not have for
              // this name, and a link built from the id lands on nothing.
              <span title="Dealroom's own address for this firm is not known here.">
                {dealroomIdText(candidate.dealroomId)}
              </span>
            ) : (
              <a href={dealroomUrl} target="_blank" rel="noreferrer" draggable={false}>
                Dealroom
              </a>
            )}
          </footer>
        </div>

        <span className="decide-stamp accept" aria-hidden="true">
          Track fund
        </span>
        <span className="decide-stamp ignore" aria-hidden="true">
          Dismiss
        </span>
      </div>
    </div>
  );
}
